import type { Image, KeyValueObject } from '~/types/global';
import type { ContentResponse, ProductClassification } from '~/server/types';
import type { ProductCategory } from '~/types/hdapi';

export interface ContentProperties {
  categoryPath?: string;
  externalId: string | number;
  [key: string]: any;
}

// Product

export type ProductType =
  | 'curtains'
  | 'pleatedBlinds'
  | 'intuPleated'
  | 'rollerBlinds'
  | 'romanBlinds'
  | 'verticalBlinds'
  | 'venetianBlinds'
  | 'wholesaleProduct'
  | 'blindScreen';

export type Alert = {
  message: string;
  variant: 'default' | 'information' | 'destructive';
};

/**
 * Represents the state of the Card CTA (Call To Action).
 */
export enum CardCtaState {
  /**
   * Quick Add: A wholesale product with no variants.
   */
  QuickAdd,

  /**
   * Configure: A made-to-measure product.
   */
  Configure,

  /**
   * Choose Option: A wholesale product with variants.
   */
  ChooseOption,

  /**
   * Add Spare: A product that can be added as a spare.
   */
  AddSpare,

  /**
   * View Product: Any product
   */
  ViewProduct
}

export interface Product {
  id: string;
  externalId: number | string;
  name: string;
  product_type: ProductType;
  classification: ProductClassification;
  fields?: ConfiguratorField[];
  images?: Image[];
  description?: string;
  specifications?: KeyValueObject[];
  product_details?: KeyValueObject[];
  carousel?: Carousel;
  variants?: ProductVariant[];
  price?: number;
  files?: File[];
  alerts?: Alert[];
  productState?: CardCtaState;
  sku?: string;
  legacySku?: string;
  canBeSample?: boolean;
  canBeSpare?: boolean;
  href?: string;
  maxQuantity?: number;
  categories?: ProductCategory[];
}

export interface ProductLayout {
  productId: string;
  externalId: number | string;
  productName: string;
  images: Image[] | undefined;
  variant?: 'wholesale' | 'default';
  description?: string;
  specifications?: KeyValueObject[];
  product_details?: KeyValueObject[];
  files?: File[];
  hasSamples?: boolean;
  content?: ContentResponse<ContentProperties>;
}

export interface File {
  name?: string;
  url: string;
  callToAction?: string;
}

export interface ProductPrice {
  price: number;
}

export interface Carousel {
  images: Image[];
}

export interface DependentRule {
  field: string[];
  type: 'min' | 'max' | 'minMax';
  formula: string;
  errorMessage: string;
}
export interface Validation {
  errorMessage: string;
  min?: number;
  max?: number;
  pattern?: RegExp;
  custom?: string;
  dependentRules?: DependentRule[];
}

export interface Condition {
  type: string;
  field?: string;
  value?: any;
  formula?: string;
  conditions?: Condition[];
}

// Configurator
export interface FieldDependency {
  field: [string];
  condition: {
    type: 'equals' | 'not' | 'greater' | 'less' | 'includes' | 'exists' | 'includesAll' | 'and' | 'or';
    value: any;
    formula?: string;
    conditions?: Condition[];
  };
  action: 'show' | 'disable' | 'validate' | 'alert' | 'setValue'; // show is only visible when condition met, disable is visible but not selectable
  validation?: Validation;
}

export interface ConfiguratorOption {
  value: string;
  label: string;
  disabled?: boolean;
  image?: string;
  dependencies?: FieldDependency[];
  helpText?: Helptext;
  description?: string;
}

export interface Helptext {
  inlineText?: string;
  tooltipText?: string;
  popupText?: string;
}

export interface ConfiguratorField {
  id: string;
  type: 'select' | 'number' | 'iconRadio' | 'radio' | 'text' | 'measurement' | 'displayText' | 'description' | 'hidden';
  group: string;
  subGroup: string; // a sub group of different things. For example 'Measurements' for width, height
  label: string;
  required: boolean;
  order: number;
  optionsOrderBy: number;
  options?: ConfiguratorOption[];
  validation?: Validation;
  dependencies?: FieldDependency[];
  defaultValue?: any;
  tooltip?: string;
  helpText?: Helptext;
  description?: string;
  reference?: string[];
}

export interface BaseProductConfiguratorField {
  form: any;
}

export interface ProductConfiguratorFieldGroup extends BaseProductConfiguratorField {
  group: string;
  fields: ConfiguratorField[];
  configurator: any;
}

export interface ProductConfiguratorField extends BaseProductConfiguratorField {
  field: ConfiguratorField;
  configurator: any;
}

export interface BaseProductConfiguratorInput {
  field: ConfiguratorField;
  componentField: any;
  isDisabled: boolean;
  hasError?: boolean;
  hasWarning?: boolean;
  validation?: any;
}

export interface ProductConfiguratorSelect extends BaseProductConfiguratorInput {
  filteredOptions: any[];
  sortedOptions: any[];
  form: any;
}

export interface ProductConfiguratorText extends BaseProductConfiguratorInput {}

export interface ProductConfiguratorMeasurement extends BaseProductConfiguratorInput {}

export interface ProductConfiguratorRadio extends BaseProductConfiguratorInput {
  filteredOptions: any[];
  form: any;
}

export interface ProductConfiguratorIconRadio extends BaseProductConfiguratorInput {
  form: any;
  filteredOptions: any[];
}

export interface ProductConfiguratorDisplayText extends BaseProductConfiguratorInput {
  filteredOptions: ConfiguratorOption[];
}

export interface ProductConfiguratorDescriptionFieldType extends BaseProductConfiguratorInput {}

// Breadcrumbs
export interface ProductBreadcrumb {
  breadcrumbs: Breadcrumb[];
}

export interface Breadcrumb {
  label: string;
  url: string;
}

// Wholesale

export interface ProductVariant {
  sku: string;
  unit: string;
  packsize?: string;
  image: Image;
  price?: number;
  salePrice?: number;
  cuttingCharge: boolean;
  offer?: string;
}
